import I18n from 'I18n';
import { useCallback, useContext, useEffect, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import QuickFilterSelect from './QuickFilterSelect';
import styled from 'foundations-theming/utils';
import { getAssociatedRecordsUtils, getAssociationInfoFromPropertySuffix, getAssociationUniqueIdentifier } from '../../../utils/associatedRecordsUtils';
import SegmentationFiltersInstanceContext from '../../../state/SegmentationFiltersInstanceContext';
// @ts-expect-error untyped
import ReferenceInputEnum from 'customer-data-reference-ui-components/ReferenceInputEnum';
import AssociatedTo from '../../../filterQueryFormat/operator/AssociatedTo';
import { useStrategy } from '../../../hooks/useStrategy';
import { getHasAssociationOptions } from '../../../strategy/strategyUtils';
import { ObjectTypeIdRegex, isAnyEventTypeId } from '../../../filterQueryFormat/ObjectTypeId';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import { Button } from 'foundations-components/transitional/components/Button';
import AssociationLabelSelect from '../../common/AssociationLabelSelect';
import { ASSOCIATION_DIRECTION } from '../../../filterQueryFormat/associations/AssociationUtilities';
import { View } from 'foundations-components/components/View';
import { Select } from 'foundations-components/transitional/components/Select';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const AssociatedRecordsQuickFilterPopoverContentContainer = styled.div.withConfig({
  displayName: "AssociatedRecordQuickFilter__AssociatedRecordsQuickFilterPopoverContentContainer",
  componentId: "jnk7h9-0"
})(["width:300px;"]);
const AssociatedRecordQuickFilter = ({
  buttonProperties,
  condition,
  disableTruncation,
  isFirst,
  property,
  onValueChange
}) => {
  const {
    data,
    props: {
      baseFilterFamily
    }
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    getValueResolver
  } = useStrategy();
  const {
    getAssociationDefinition,
    getObjectTypeIdFromPropertySuffix,
    getObjectTypeOptions,
    getCustomObjectReferenceResolver,
    getDisabledAssociationOptions
  } = getAssociatedRecordsUtils(data, baseFilterFamily);
  const prevAssociatedObjectTypeId = getObjectTypeIdFromPropertySuffix(condition === null || condition === void 0 ? void 0 : condition.propertySuffix);
  const [isOpen, setIsOpen] = useState(false);
  const [associatedObjectTypeId, setAssociatedObjectTypeId] = useState(prevAssociatedObjectTypeId);
  const [objectId, setObjectId] = useState();
  const [associationInverseId, setAssociationInverseId] = useState();
  const [associationCategory, setAssociationCategory] = useState();
  const options = getObjectTypeOptions(prevAssociatedObjectTypeId);
  const isAssnFilterFamily = associatedObjectTypeId && ObjectTypeIdRegex.test(associatedObjectTypeId) && !isAnyEventTypeId(associatedObjectTypeId);
  const showAssociationSelect = associatedObjectTypeId && objectId && isAssnFilterFamily && getHasAssociationOptions(baseFilterFamily, associatedObjectTypeId, data, ASSOCIATION_DIRECTION.INVERSE);
  useEffect(() => {
    if (condition) {
      const {
        propertySuffix,
        value
      } = condition;
      setAssociatedObjectTypeId(getObjectTypeIdFromPropertySuffix(propertySuffix));
      setObjectId(value);
      const {
        associationId: newAssociationInverseId,
        associationCategory: newAssociationCategory
      } = getAssociationInfoFromPropertySuffix(propertySuffix);
      setAssociationInverseId(newAssociationInverseId);
      setAssociationCategory(newAssociationCategory);
    } else {
      setAssociatedObjectTypeId(undefined);
    }
    // below disabled because of `getObjectTypeIdFromPropertySuffix`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition]);
  const onToggleOpen = () => {
    setIsOpen(currentIsOpen => !currentIsOpen);
  };
  const onObjectTypeInputChange = evt => {
    const newAssociatedObjectTypeId = evt.target.value;
    setAssociatedObjectTypeId(newAssociatedObjectTypeId);
    setObjectId(undefined);
  };
  const onObjectIdInputChange = evt => {
    setObjectId(evt.target.value);
    setAssociationInverseId(undefined);
    setAssociationCategory(undefined);
  };
  const onAssociationValueChange = (newAssociationInverseId, newAssociationCategory) => {
    setAssociationInverseId(newAssociationInverseId);
    setAssociationCategory(newAssociationCategory);
  };
  const onApplyChanges = useCallback(() => {
    if (associatedObjectTypeId && objectId) {
      const associationDefinition = getAssociationDefinition(associatedObjectTypeId, associationInverseId, associationCategory);
      if (associationDefinition) {
        const associationUniqueIdentifier = getAssociationUniqueIdentifier(associationDefinition);
        if (associationUniqueIdentifier) {
          onValueChange(AssociatedTo.of(property, associationUniqueIdentifier, objectId));
        }
      }
      setIsOpen(false);
    }
  }, [associatedObjectTypeId, associationCategory, associationInverseId, getAssociationDefinition, objectId, onValueChange, property]);
  const onClearChanges = useCallback(() => {
    if (condition) {
      onValueChange();
    } else {
      setAssociatedObjectTypeId(undefined);
    }
  }, [condition, onValueChange]);

  // if there are user configured disabled associations, filter them out
  // but keep the option if its already in use
  const disabledAssociationOptions = getDisabledAssociationOptions();
  const filteredOptions = options.filter(option => {
    if (option.value === associatedObjectTypeId) {
      return true;
    }
    return !disabledAssociationOptions.includes(option.value);
  });
  return /*#__PURE__*/_jsx(QuickFilterSelect, {
    property: property,
    buttonProperties: buttonProperties,
    buttonText: property.displayName,
    condition: condition,
    disableTruncation: disableTruncation,
    isActive: !!(condition !== null && condition !== void 0 && condition.value && condition.propertySuffix),
    isFirst: isFirst,
    isOpen: isOpen,
    popoverContent: {
      body: /*#__PURE__*/_jsxs(AssociatedRecordsQuickFilterPopoverContentContainer, {
        children: [/*#__PURE__*/_jsx(FormLabel, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.quickFiltersBar.associatedRecord.label"
          })
        }), /*#__PURE__*/_jsx(Select, {
          onChange: onObjectTypeInputChange,
          options: filteredOptions,
          value: associatedObjectTypeId
        }), associatedObjectTypeId && /*#__PURE__*/_jsx(FormControl, {
          "aria-label": I18n.text('customerDataFiltersUI.customerDataFilters.FilterOperatorInput.ariaLabel'),
          children: /*#__PURE__*/_jsx(View, {
            as: "div",
            marginBlockStart: "200",
            children: /*#__PURE__*/_jsx(ReferenceInputEnum
            // used for rerendering options
            , {
              onChange: onObjectIdInputChange,
              resolver: getCustomObjectReferenceResolver(getValueResolver(baseFilterFamily, AssociatedTo.of(property)), associatedObjectTypeId),
              value: objectId
            }, associatedObjectTypeId)
          })
        }), associatedObjectTypeId && showAssociationSelect && /*#__PURE__*/_jsx(AssociationLabelSelect, {
          associatedObjectTypeId: associatedObjectTypeId,
          associationCategory: associationCategory,
          associationInverseId: associationInverseId,
          onAssociationValueChange: onAssociationValueChange
        })]
      }),
      footer: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Button, {
          "data-selenium-test": "AssociatedRecordsQuickFilter-apply",
          disabled: !(associatedObjectTypeId && objectId),
          size: "small",
          use: "tertiary",
          onClick: onApplyChanges,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.quickFiltersBar.associatedRecord.actions.apply"
          })
        }), /*#__PURE__*/_jsx(Button, {
          "data-selenium-test": "AssociatedRecordsQuickFilter-clear",
          disabled: !associatedObjectTypeId,
          size: "small",
          use: "tertiary-light",
          onClick: onClearChanges,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.quickFiltersBar.associatedRecord.actions.clear"
          })
        })]
      })
    },
    onToggleOpen: onToggleOpen,
    onValueChange: onValueChange
  });
};
export default AssociatedRecordQuickFilter;